import React from "react";
import { StyledLabel, StyledTextInput } from "@aureskonnect/react-ui";
import { AvGroup } from "availity-reactstrap-validation";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { ipMatchWord } from "@constants/index";
import { POS_CODE_CAISSE_LENGTH, POS_CODE_CAISSE_REGEX } from "@constants/pos";

type ApplicationFormProps = {
    designation: string;
    setDesignation: (value: string) => void;
    inputDesignation: boolean;
    setInputDesignation: (value: boolean) => void;
    isApplicationListExist: boolean;
    setIsApplicationListExist: (value: boolean) => void;
    localNamesApplications: string[];
    setIsEdited: (value: boolean) => void;
    setIsValidateButtonClicked: (value: boolean) => void;
    ip: string;
    setIp: (value: string) => void;
    isIpExist?: boolean;
    setIsIpExist?: (value: boolean) => void;
    localIps?: string[];
    remark: string;
    setRemark: (value: string) => void;
    matchWordDesignation: RegExp;
    codeCaisse?: string;
    setCodeCaisse?: (value: string) => void;
    isCodeCaisseRequired?: boolean;
    setIsCodeCaisseRequired?: (value: boolean) => void;
    isCodeCaisseDuplicate?: boolean;
    setIsCodeCaisseDuplicate?: (value: boolean) => void;
    localCodesCaisse?: string[];
};

export const ApplicationForm: React.FC<ApplicationFormProps> = ({
    designation,
    setDesignation,
    inputDesignation,
    setInputDesignation,
    isApplicationListExist,
    setIsApplicationListExist,
    localNamesApplications,
    setIsEdited,
    setIsValidateButtonClicked,
    ip,
    setIp,
    isIpExist = false,
    setIsIpExist,
    localIps = [],
    remark,
    setRemark,
    matchWordDesignation,
    codeCaisse,
    setCodeCaisse,
    isCodeCaisseRequired,
    setIsCodeCaisseRequired,
    isCodeCaisseDuplicate,
    setIsCodeCaisseDuplicate,
    localCodesCaisse = [],
}) => {
    const { t } = useTranslation();
    const showCodeCaisse = setCodeCaisse !== undefined;

    const handleCodeCaisseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digitsOnly = e.target.value
            .replace(/\D/g, "")
            .slice(0, POS_CODE_CAISSE_LENGTH);
        setCodeCaisse?.(digitsOnly);
        setIsEdited(true);
        setIsValidateButtonClicked(true);
        setIsCodeCaisseRequired?.(false);
        setIsCodeCaisseDuplicate?.(
            digitsOnly.length === POS_CODE_CAISSE_LENGTH &&
                localCodesCaisse.includes(digitsOnly)
        );
    };

    const handleDesignationChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const val = e.target.value;
        const trimmedVal = val.trim().toUpperCase();

        if (localNamesApplications.includes(trimmedVal)) {
            setIsApplicationListExist(true);
        } else {
            setIsApplicationListExist(false);
        }
        setDesignation(val);
        setIsEdited(true);
        setIsValidateButtonClicked(true);
        setInputDesignation(false);
    };

    const handleIpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value || "";
        setIsValidateButtonClicked(true);
        setIp(val);
        setIsEdited(true);
        if (setIsIpExist) {
            setIsIpExist(val !== "" && localIps.includes(val));
        }
    };

    const handleRemarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsValidateButtonClicked(true);
        setRemark(e.target.value);
        setIsEdited(true);
    };

    return (
        <div className="pl-3 pr-5 card-body">
            <AvGroup>
                <StyledLabel
                    htmlFor="designation"
                    className="required__clz mt-3"
                >
                    {t("Designation")}
                </StyledLabel>
                <StyledTextInput
                    autoComplete="off"
                    className={classnames("afa_inp_designation", {
                        input__clz:
                            inputDesignation ||
                            isApplicationListExist ||
                            (designation.trim() === "" && designation !== ""),
                    })}
                    id="designation"
                    name="designation"
                    placeholder={t("Write")}
                    type="text"
                    onChange={handleDesignationChange}
                    value={designation}
                    validate={{
                        pattern: {
                            value: matchWordDesignation,
                            errorMessage: t("Prohibited characters"),
                        },
                    }}
                    autoFocus
                />
                {inputDesignation ||
                (designation.trim() === "" && designation !== "") ? (
                    <div
                        style={{
                            width: "100%",
                            marginTop: "0.25rem",
                            fontSize: "80%",
                            color: "#f46a6a",
                        }}
                    >
                        {t("Please enter a designation")}
                    </div>
                ) : null}
                {isApplicationListExist ? (
                    <div
                        style={{
                            width: "100%",
                            marginTop: "0.25rem",
                            fontSize: "80%",
                            color: "#f46a6a",
                        }}
                    >
                        {t("The name is already exists")!}
                    </div>
                ) : null}
            </AvGroup>

            {showCodeCaisse && (
                <AvGroup>
                    <StyledLabel
                        htmlFor="codeCaisse"
                        className="required__clz mt-3"
                    >
                        {t("Cash register code")}
                    </StyledLabel>
                    <StyledTextInput
                        autoComplete="off"
                        id="codeCaisse"
                        name="codeCaisse"
                        data-testid="pos-add-application-code-caisse-input"
                        placeholder={t("Write")}
                        type="text"
                        inputMode="numeric"
                        maxLength={POS_CODE_CAISSE_LENGTH}
                        aria-required="true"
                        onChange={handleCodeCaisseChange}
                        value={codeCaisse ?? ""}
                        className={classnames("afa_inp_codeCaisse", {
                            input__clz:
                                isCodeCaisseRequired ||
                                isCodeCaisseDuplicate ||
                                ((codeCaisse ?? "").length > 0 &&
                                    !POS_CODE_CAISSE_REGEX.test(
                                        codeCaisse ?? ""
                                    )),
                        })}
                    />
                    {isCodeCaisseRequired && (
                        <div
                            role="alert"
                            data-testid="pos-add-application-code-caisse-required-error"
                            style={{
                                width: "100%",
                                marginTop: "0.25rem",
                                fontSize: "80%",
                                color: "#f46a6a",
                            }}
                        >
                            {t("Please enter a cash register code")}
                        </div>
                    )}
                    {!isCodeCaisseRequired &&
                        (codeCaisse ?? "").length > 0 &&
                        !POS_CODE_CAISSE_REGEX.test(codeCaisse ?? "") && (
                            <div
                                role="alert"
                                data-testid="pos-add-application-code-caisse-format-error"
                                style={{
                                    width: "100%",
                                    marginTop: "0.25rem",
                                    fontSize: "80%",
                                    color: "#f46a6a",
                                }}
                            >
                                {t(
                                    "The cash register code must be 2 digits between 01 and 99"
                                )}
                            </div>
                        )}
                    {isCodeCaisseDuplicate && (
                        <div
                            role="alert"
                            data-testid="pos-add-application-code-caisse-duplicate-error"
                            style={{
                                width: "100%",
                                marginTop: "0.25rem",
                                fontSize: "80%",
                                color: "#f46a6a",
                            }}
                        >
                            {t("This cash register code already exists")}
                        </div>
                    )}
                </AvGroup>
            )}

            <AvGroup>
                <StyledLabel htmlFor="ip" className="mt-3">
                    {t("IP address")}
                </StyledLabel>
                <StyledTextInput
                    id="ip"
                    name="ip"
                    autoComplete="off"
                    placeholder={t("Write")}
                    type="text"
                    onChange={handleIpChange}
                    className={classnames("afa_inp_ipAddress", {
                        input__clz:
                            (ip !== undefined &&
                                !ip.match(ipMatchWord) &&
                                ip !== "") ||
                            isIpExist,
                    })}
                    value={ip}
                />

                {ip !== undefined && !ip.match(ipMatchWord) && ip !== "" && (
                    <div
                        style={{
                            width: "100%",
                            marginTop: "0.25rem",
                            fontSize: "80%",
                            color: "#f46a6a",
                        }}
                    >
                        {t("IP address is invalid")!}
                    </div>
                )}
                {isIpExist && (
                    <div
                        style={{
                            width: "100%",
                            marginTop: "0.25rem",
                            fontSize: "80%",
                            color: "#f46a6a",
                        }}
                    >
                        {t("This IP address is already in use")!}
                    </div>
                )}
            </AvGroup>

            <AvGroup>
                <StyledLabel className="mt-3" htmlFor="remark">
                    {t("Remark")}
                </StyledLabel>
                <StyledTextInput
                    autoComplete="off"
                    id="remark"
                    name="remark"
                    placeholder={t("Write")}
                    type="text"
                    onChange={handleRemarkChange}
                    value={remark}
                    className="afa_inp_remark"
                />
            </AvGroup>
        </div>
    );
};
